import { State } from "api/entities/enums";
import { CommissionFilter } from "api/filters/commission";
import format from "date-fns/format";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { startOfMonth, endOfMonth } from "date-fns";
import { TitleProvider } from "commons/DataManagement/TitleProvider";
import styles from "styles/UserListing.module.css";
import TableComponent from "commons/table/table.component";
import CommissionsInactiveTableColumn from "./inactive/table/commissions-inactive-table-column";
import CommissionsInactiveTableRow from "./inactive/table/commissions-inactive-table-row";
import ExpandedRow from "./ExpandedRow/ExpandedRow";
import { sessionStorageHelper } from "commons/Helpers/sessionStorageHelper";
import Button from "commons/Button/Button";
import useAppContext from "context/useContext";
import api from "api";
import { useErrorHandling } from "commons/hooks/useErrorHandling";
import { CommissionForAuditorCompany } from "api/entities/commission";
import { QueryParams } from "api/backend";
import i18next from "i18next";
import DatePicker from "react-datepicker";
import { getLocale } from "i18n";
import { LabelOrderElement } from "commons/DataManagement/DataList";
import FileSaver from "file-saver";
import Pagination from "commons/Pagination";
import commissionsInactiveTableHelper from "./inactive/commissions-inactive-table-helper";
import CommissionsInactiveOrder from "./inactive/table/types/commissions-inactive-order";
import CommissionsInactivePagination from "./inactive/table/types/commissions-inactive-request-pagination";

const CommissionsInactive: FC = () => {
  const { t } = useTranslation(["auditorManagerView", "services"], { keyPrefix: "commissions" });
  const { refetchCounters, user, selectedAppLanguage } = useAppContext();
  const { handleErrors } = useErrorHandling();

  const [fetchStatus, setFetchStatus] = useState(State.completed);
  const [data, setData] = useState<CommissionForAuditorCompany[]>([]);
  const [expandedRows, setExpandedRows] = useState<string[]>([]);

  const [filter, setFilter] = useState<CommissionFilter>({
    target_date: startOfMonth(new Date()),
    target_date_end: endOfMonth(new Date()),
    commission_no: "",
    address_primary_name: "",
    address_secondary_name: "",
    locomotive: "",
    service_name: "",
  });

  const [order, setOrder] = useState<CommissionsInactiveOrder>({
    column: "identifier",
    descending: true,
  });

  const [pagination, setPagination] = useState<CommissionsInactivePagination>({
    page: 1,
    pages: 1,
  });

  const [isLoading, setIsLoading] = useState(false);

  const refetch = () => {
    if (!user) return;

    const fetchFun =
      fetchStatus === State.completed
        ? api.fetchCommissionsFinishedForAuditorCompany
        : api.fetchCommissionsCanceledForAuditorCompany;

    const query: QueryParams<CommissionForAuditorCompany[], CommissionFilter> = {
      filter: filter,
      order_by: order.column,
      descending: order.descending,
      page: pagination.page,
    };

    fetchFun(user?.company_id!, query)
      .then((response) => {
        if (handleErrors(response.error).ok) {
          setData(response.data);
          setPagination({
            ...pagination,
            pages: response.pages ?? 1,
          });
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const fetchCSV = () => {
    if (!user) return;

    const query: QueryParams<File, CommissionFilter, CommissionForAuditorCompany> = {
      filter: filter,
      order_by: order.column,
      descending: order.descending,
    };

    api.fetchFinishedCommissionsCSVForAuditorCompany(user.company_id!, query).then((response) => {
      if (handleErrors(response.error).ok) {
        var date = new Date();

        const fileName = `${t("csvFilenameTemplate").replace(
          ":date",
          `${date.getDate()}.${date.getMonth() + 1}.${date.getFullYear()}_${date.getHours()}_${date.getMinutes()}`,
        )}.csv`;

        FileSaver.saveAs(response.data, fileName);
      }
    });
  };
  const apiCallRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (apiCallRef.current) {
      clearTimeout(apiCallRef.current);
    }
    setIsLoading(true);
    apiCallRef.current = setTimeout(refetch, 150);
    // eslint-disable-next-line
  }, [fetchStatus, filter, order, pagination.page, user]);

  const onRowClick = (humanId: string) => {
    if (expandedRows.find((row) => row === humanId)) {
      setExpandedRows(expandedRows.filter((row) => row !== humanId));
    } else {
      setExpandedRows([...expandedRows, humanId]);
    }
  };

  const getTableRow = (item: CommissionForAuditorCompany): CommissionsInactiveTableRow => {
    const isHiddenComponentVisible = expandedRows.find((row) => row === item.identifier) !== undefined;

    const targetDate = format(new Date(item.target_date), "dd.MM.yyyy - HH:mm");

    return {
      id: item.identifier,
      value: {
        targetDate: targetDate,
        companyName: item.dispatcher.user.company.company_name,
        humanId: item.identifier,
        trainNumber: item.train_no,
        address: item.address_primary.name,
        addressSecondary: item.address_secondary?.name ?? "---",
        locomotive: item.locomotive ?? "---",
        service: i18next.t(item.service.name, { ns: "services" }),
        auditor: item.auditor ?? "---",
      },
      onClick: () => onRowClick(item.identifier),
      hiddenComponent: <ExpandedRow data={item} expand={isHiddenComponentVisible} reloadOneRow={refetch} />,
      isHiddenComponentVisible: isHiddenComponentVisible,
    };
  };

  const columns = useMemo(() => {
    const columns: CommissionsInactiveTableColumn[] = [
      {
        accessor: "targetDate",
        header: (
          <div className={styles.labelWrapper}>
            <DatePicker
              dayClassName={(date) => {
                if (commissionsInactiveTableHelper.areDatesEqual(date, filter.target_date_end)) {
                  return "react-datepicker__day--selected";
                }
                return null;
              }}
              onChange={(date) => {
                const [start, end] = date;
                setFilter({ ...filter, target_date: start ?? undefined, target_date_end: end ?? undefined });
              }}
              selected={filter.target_date}
              startDate={filter.target_date}
              endDate={filter.target_date_end}
              dateFormat="dd-MM-yyyy"
              locale={getLocale()}
              customInput={<input className={styles.input} />}
              placeholderText={t("data.targetDate")!}
              selectsRange
              isClearable
              closeOnScroll
            />
            <LabelOrderElement
              orderable
              descending={order.column === "target_date" ? order.descending : undefined}
              onClick={() => {
                if (order.column !== "target_date") {
                  setOrder({
                    column: "target_date",
                    descending: true,
                  });
                } else {
                  setOrder({ ...order, descending: !order.descending });
                }
              }}
            />
          </div>
        ),
        colSpan: 3,
      },
      {
        accessor: "companyName",
        header: (
          <div className={styles.labelWrapper}>
            <div className={styles.labelText}>{t("data.coName")}</div>
            <LabelOrderElement
              orderable
              descending={order.column === "dispatcher_co_name" ? order.descending : undefined}
              onClick={() => {
                if (order.column !== "dispatcher_co_name") {
                  setOrder({
                    column: "dispatcher_co_name",
                    descending: true,
                  });
                } else {
                  setOrder({ ...order, descending: !order.descending });
                }
              }}
            />
          </div>
        ),
        colSpan: 2,
      },
      {
        accessor: "humanId",
        header: (
          <div className={styles.labelWrapper}>
            <input
              className={styles.input}
              onChange={(event) => {
                const value = event.currentTarget.value;
                if (!value) {
                  setFilter({ ...filter, identifier: undefined });
                  return;
                }

                const numberPattern = /\d*/;
                if (numberPattern.test(value)) setFilter({ ...filter, identifier: Number(value) });
              }}
              value={filter.identifier}
              placeholder={t("data.identifier")!}
            />
            <LabelOrderElement
              orderable
              descending={order.column === "identifier" ? order.descending : undefined}
              onClick={() => {
                if (order.column !== "identifier") {
                  setOrder({
                    column: "identifier",
                    descending: true,
                  });
                } else {
                  setOrder({ ...order, descending: !order.descending });
                }
              }}
            />
          </div>
        ),
        colSpan: 2,
      },
      {
        accessor: "trainNumber",
        header: (
          <div className={styles.labelWrapper}>
            <input
              className={styles.input}
              onChange={(event) => {
                const value = event.currentTarget.value;
                setFilter({ ...filter, commission_no: value });
              }}
              value={filter.commission_no}
              placeholder={t("data.trainNo")!}
            />
            <LabelOrderElement
              orderable
              descending={order.column === "train_no" ? order.descending : undefined}
              onClick={() => {
                if (order.column !== "train_no") {
                  setOrder({
                    column: "train_no",
                    descending: true,
                  });
                } else {
                  setOrder({ ...order, descending: !order.descending });
                }
              }}
            />
          </div>
        ),
        colSpan: 2,
      },
      {
        accessor: "address",
        header: (
          <div className={styles.labelWrapper}>
            <input
              className={styles.input}
              onChange={(event) => {
                const value = event.currentTarget.value;
                setFilter({ ...filter, address_primary_name: value });
              }}
              value={filter.address_primary_name}
              placeholder={t("data.address")!}
            />
            <LabelOrderElement
              orderable
              descending={order.column === "address_primary_name" ? order.descending : undefined}
              onClick={() => {
                if (order.column !== "address_primary_name") {
                  setOrder({
                    column: "address_primary_name",
                    descending: true,
                  });
                } else {
                  setOrder({ ...order, descending: !order.descending });
                }
              }}
            />
          </div>
        ),
        colSpan: 2,
      },
      {
        accessor: "addressSecondary",
        header: (
          <div className={styles.labelWrapper}>
            <input
              className={styles.input}
              onChange={(event) => {
                const value = event.currentTarget.value;
                setFilter({ ...filter, address_secondary_name: value });
              }}
              value={filter.address_secondary_name}
              placeholder={t("data.addressSecondary")!}
            />
            <LabelOrderElement
              orderable
              descending={order.column === "address_secondary_name" ? order.descending : undefined}
              onClick={() => {
                if (order.column !== "address_secondary_name") {
                  setOrder({
                    column: "address_secondary_name",
                    descending: true,
                  });
                } else {
                  setOrder({ ...order, descending: !order.descending });
                }
              }}
            />
          </div>
        ),
        colSpan: 2,
      },
      {
        accessor: "locomotive",
        header: (
          <div className={styles.labelWrapper}>
            <input
              className={styles.input}
              onChange={(event) => {
                const value = event.currentTarget.value;
                setFilter({ ...filter, locomotive: value });
              }}
              value={filter.locomotive}
              placeholder={t("data.locomotive")!}
            />
            <LabelOrderElement
              orderable
              descending={order.column === "locomotive" ? order.descending : undefined}
              onClick={() => {
                if (order.column !== "locomotive") {
                  setOrder({
                    column: "locomotive",
                    descending: true,
                  });
                } else {
                  setOrder({ ...order, descending: !order.descending });
                }
              }}
            />
          </div>
        ),
        colSpan: 2,
      },
      {
        accessor: "service",
        header: (
          <div className={styles.labelWrapper}>
            <input
              className={styles.input}
              onChange={(event) => {
                const value = event.currentTarget.value;
                setFilter({ ...filter, service_name: value });
              }}
              value={filter.service_name}
              placeholder={t("data.service")!}
            />
            <LabelOrderElement
              orderable
              descending={order.column === "service_name" ? order.descending : undefined}
              onClick={() => {
                if (order.column !== "service_name") {
                  setOrder({
                    column: "service_name",
                    descending: true,
                  });
                } else {
                  setOrder({ ...order, descending: !order.descending });
                }
              }}
            />
          </div>
        ),
        colSpan: 2,
      },
    ];

    if (fetchStatus !== State.canceled && sessionStorageHelper.getAspects().includes("operator")) {
      columns.push({
        accessor: "auditor",
        header: (
          <div className={styles.labelWrapper}>
            <input
              className={styles.input}
              onChange={(event) => {
                const value = event.currentTarget.value;
                setFilter({ ...filter, auditor: value });
              }}
              value={filter.auditor}
              placeholder={t("data.auditor")!}
            />
            <LabelOrderElement
              orderable
              descending={order.column === "auditor" ? order.descending : undefined}
              onClick={() => {
                if (order.column !== "auditor") {
                  setOrder({
                    column: "auditor",
                    descending: true,
                  });
                } else {
                  setOrder({ ...order, descending: !order.descending });
                }
              }}
            />
          </div>
        ),
        colSpan: 2,
      });
    }

    return columns;
    // eslint-disable-next-line
  }, [t, selectedAppLanguage, fetchStatus, filter, order]);

  const rows = useMemo(() => {
    return data.map(getTableRow);
    // eslint-disable-next-line
  }, [data, selectedAppLanguage, expandedRows]);

  return (
    <>
      <TitleProvider title={`${t("title_inactive")} | Raily Marketplace`} />
      <div className={styles.content}>
        <div className={styles.content_row}>
          <h1 className={styles.h1}> {t("title_inactive")} </h1>
        </div>
        <div className={styles.content_row}>
          <div className={styles.row_items}>
            <Button
              type="button"
              label={t("button_completed")}
              className={fetchStatus === State.completed ? styles.button_active : styles.button_inactive}
              click={() => {
                if (fetchStatus !== State.completed) {
                  setFetchStatus(State.completed);
                }
              }}
            />
            <Button
              type="button"
              label={t("button_canceled")}
              className={fetchStatus === State.canceled ? styles.button_active : styles.button_inactive}
              click={() => {
                if (fetchStatus !== State.canceled) {
                  setFetchStatus(State.canceled);
                }
              }}
            />
          </div>
          <div className={styles.row_items}>
            {fetchStatus === State.completed && <Button type="button" label={t("genCSV")} click={fetchCSV} />}
            <Button
              type="button"
              label={t("refresh")}
              click={() => {
                refetch();
                refetchCounters();
              }}
            />
          </div>
        </div>
        <div className={styles.table_wrapper}>
          <TableComponent columns={columns} rows={rows} isLoading={isLoading} />
        </div>
        <div className={styles.row_items}>
          <Pagination
            page={pagination.page}
            pages={pagination.pages}
            change={(page) => {
              setPagination({ ...pagination, page });
            }}
          />
        </div>
      </div>
    </>
  );
};

export default CommissionsInactive;
